import { FieldBlock, FieldType, TextStyle } from '@coast/schemas';
import moment from 'moment';
import React, { ReactElement, ReactNode, useContext, useMemo } from 'react';
import { View } from '@shared/components/View';
import { useLayoutConfig } from '@shared/components/blocks/contexts/layoutConfig';
import { CardLayoutContext, CardRenderContext } from '@shared/components/blocks/contexts/render';
import { CardThemeContext } from '@shared/components/blocks/contexts/theme';
import { Reminder, TodoItem } from '@shared/components/blocks/types';
import { getTypoComponent } from '@shared/components/blocks/utils';
import { MBBlockLabel } from '@shared/components/blocks/utils/label';
import { MBImage } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { ColorThemeContext, grid } from '@shared/styles';
import {
  formatDateTimeWithYear,
  formatDateWithYear,
  formatRelativeTime,
  formatTime,
} from '@shared/util/date';
import { i18n } from '@shared/util/i18n';
import { renderIcon } from './input/utils';
import { AddressContent } from './inputAddress/content';
import { GeolocationContent } from './inputGeolocation/content';
import { formatNumber } from './inputNumber/util';
import { toString as recurrenceToString } from './inputRecurrence/utils';
import { format as formatReminder } from './inputReminder/hooks';

interface Props {
  value: any;
  block: FieldBlock;
  onPress?(): void;
  presenter?(
    value: any,
    fieldType: FieldType | undefined,
    textStyle: TextStyle | undefined,
    textColor: string,
  ): ReactElement;
  renderLeft?(): ReactElement;
}

export function defaultFieldPresenter(
  value: any,
  fieldType: FieldType | undefined,
  textStyle: TextStyle,
  textColor: string,
): ReactElement {
  const TypoComponent = getTypoComponent(textStyle);
  let content: ReactNode;
  switch (fieldType) {
    case 'cards':
      content = i18n('card.blocks.cards.value', { count: value?.length ?? 0 });
      break;
    case 'text':
    case 'textarea':
      content = value;
      break;
    case 'geolocation':
      content = <GeolocationContent geolocation={value} />;
      break;
    case 'address':
      content = <AddressContent textStyle={textStyle} address={value} />;
      break;
    case 'date':
      content = formatDateWithYear(value);
      break;
    case 'time':
      content = formatTime(value);
      break;
    case 'relativeTime':
      content = formatRelativeTime(value);
      break;
    case 'float':
    case 'percent':
    case 'currency':
      content = formatNumber(value, fieldType);
      break;
    case 'datetime':
      content = formatDateTimeWithYear(value);
      break;
    case 'recurrence':
      content = value ? recurrenceToString(value) : '';
      break;
    case 'toggle':
      // TODO(fant): how do we display toggle fields?
      content = value ? '✅' : '🔴';
      break;
    case 'reminder':
      content = value.map((r: Reminder) => formatReminder(moment.duration(r.duration))).join(', ');
      break;
    case 'todo':
      const items: TodoItem[] = value;
      content = i18n('card.blocks.todo.summary', {
        completedCount: items.filter(item => item.status === 'COMPLETED').length,
        count: items.length,
      });
      break;
    case 'signature':
      content = (
        <View style={{ height: grid(12) }}>
          <MBImage source={{ uri: value.url }} fill />
        </View>
      );
      break;
    default:
      if (typeof value === 'object') {
        content = JSON.stringify(value);
      } else {
        content = value;
      }
      break;
  }
  return (
    <CardLayoutContext.Consumer>
      {layoutMode => (
        <CardRenderContext.Consumer>
          {renderContext => (
            <View centerVertically>
              {typeof content === 'string' ? (
                <TypoComponent
                  allowFontScaling={false}
                  color={textColor}
                  ellipsizeMode="tail"
                  numberOfLines={(() => {
                    if (['agenda', 'calendar'].includes(renderContext) || layoutMode === 'row') {
                      return 1;
                    }
                  })()}
                >
                  {content}
                </TypoComponent>
              ) : (
                content
              )}
            </View>
          )}
        </CardRenderContext.Consumer>
      )}
    </CardLayoutContext.Consumer>
  );
}

export function MBFieldBlock({
  value,
  block,
  onPress,
  presenter = defaultFieldPresenter,
  renderLeft,
}: Props) {
  const stale = useContext(CardThemeContext) === 'stale';
  const negative = typeof value === 'number' && value < 0;
  const colors = useContext(ColorThemeContext);
  const renderContext = useContext(CardRenderContext);
  const {
    staticBlock: { paddingLeft, displayLabel },
  } = useLayoutConfig();
  const textColor = negative
    ? colors.error.foreground
    : stale
    ? colors.foregroundInactive
    : colors.foregroundActive;
  const content = useMemo(
    () => presenter(value, block.fieldType, block.textStyle, textColor),
    [value, block.fieldType, block.textStyle, presenter, textColor],
  );

  const leftContent = useMemo((): ReactElement => {
    if (renderLeft) {
      return renderLeft();
    }
    return renderIcon(block.icon, block.name);
  }, [renderLeft, block]);
  const label = useMemo(() => {
    const shouldDisplayLabel = !!block.label && displayLabel;
    return shouldDisplayLabel ? <MBBlockLabel label={block.label} /> : null;
  }, [block, displayLabel]);

  const isCollectionView = ['list', 'table'].includes(renderContext);
  let iconPad = isCollectionView ? 1 : 2;
  if (renderContext === 'calendar') {
    iconPad = 0;
  }

  return (
    <MBTouchableOpacity activeOpacity={1} onPress={onPress}>
      <View row paddingLeft={paddingLeft} paddingRight={4} paddingVertical={!!label ? 0.5 : 0}>
        {!!leftContent && leftContent}
        {!!leftContent && <View paddingRight={iconPad} />}
        <View flex centerVertically>
          {label}
          {content}
        </View>
      </View>
    </MBTouchableOpacity>
  );
}
